import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Group,
  Stack,
  Text,
  useMantineTheme,
  useMantineColorScheme,
  Loader,
  Center,
  ActionIcon,
  Divider,
  Modal,
  Rating,
  Flex,
  Button,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { BookmarkSimpleIcon, XIcon } from "@phosphor-icons/react";
import { useAllBookmarkedSessions, useMe } from "@/api";
import { usePaths } from "@/api";
import { useUpdateSession } from "@/api";

const SettingsScreen: React.FC = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const { data: me } = useMe();

  const [modalOpen, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [sessionToDelete, setSessionToDelete] = useState<any>(null);

  const { data: bookmarkedSessions = [], isLoading: isLoadingBookmarks, refetch: refetchBookmarks } = 
    useAllBookmarkedSessions();

  const { data: allPaths, isLoading: isLoadingPaths } = usePaths();

  const { mutateAsync: updatePathSession } = useUpdateSession();

  const groupedBookmarks = useMemo(() => {
    const sessionsArray = Array.isArray(bookmarkedSessions) 
      ? bookmarkedSessions 
      : bookmarkedSessions?.results || [];
      
    if (!sessionsArray.length || !allPaths?.results) return {};

    const groups: { [key: string]: any[] } = {};

    sessionsArray.forEach((session: any) => {
      const path = allPaths.results.find((p: any) => p.id === session.path);
      const pathName = path?.name || "Unknown Path";

      if (!groups[pathName]) {
        groups[pathName] = [];
      }

      const hasBookmarkedRecommendations = session.recommendations?.some((rec: any) => rec.is_bookmarked);
      
      if (hasBookmarkedRecommendations) {
        groups[pathName].push({
          sessionTitle: session.title,
          sessionId: session.id,
          pathId: session.path,
          bookmarkedCount: session.recommendations?.filter((rec: any) => rec.is_bookmarked).length || 0,
          session: session 
        });
      }
    });

    return groups;
  }, [bookmarkedSessions, allPaths]);

  const filteredBookmarks = useMemo(() => {
    return groupedBookmarks;
    
    /*
    if (!searchQuery.trim()) return groupedBookmarks;

    const filtered: { [key: string]: any[] } = {};
    
    Object.entries(groupedBookmarks).forEach(([pathName, sessions]) => {
      const filteredSessions = sessions.filter((session) => {
        const matchesTitle = session.sessionTitle.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesPath = pathName.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesRecommendations = session.session?.recommendations?.some((rec: any) => 
          rec.is_bookmarked && 
          rec.prompt_text?.toLowerCase().includes(searchQuery.toLowerCase())
        );
        
        return matchesTitle || matchesPath || matchesRecommendations;
      });
      
      if (filteredSessions.length > 0) {
        filtered[pathName] = filteredSessions;
      }
    });

    return filtered;
    */
  }, [groupedBookmarks]); // Remove searchQuery from dependencies

  const handleBookmarkClick = (session: any) => {
    setSelectedSession(session.session);
    openModal();
  };

  const handleRemoveSessionBookmark = async (session: any) => {
    setSessionToDelete(session);
    openDeleteModal();
  };

  const confirmDeleteBookmark = async () => {
    if (!sessionToDelete) return;
    
    try {
      const sessionsArray = Array.isArray(bookmarkedSessions)
        ? bookmarkedSessions
        : bookmarkedSessions?.results || [];

      const sessionToUpdate = sessionsArray.find((s: any) => s.id === sessionToDelete.sessionId);
      if (!sessionToUpdate) return;

      const updatedRecommendations = sessionToUpdate.recommendations.map((rec: any) => ({
        ...rec,
        is_bookmarked: false
      }));

      await updatePathSession({
        id: sessionToUpdate.id,
        data: { recommendations: updatedRecommendations }
      });

      refetchBookmarks();
      closeDeleteModal();
      setSessionToDelete(null);
    } catch (error) {
      console.error("Error removing bookmark:", error);
    }
  };

  const cancelDeleteBookmark = () => {
    closeDeleteModal();
    setSessionToDelete(null);
  };

  const handleUpdateRating = async (recommendation: any, newRating: number) => {
    try {
      await updatePathSession({
        id: selectedSession.id,
        data: {
          recommendations: selectedSession?.recommendations?.map((rec: any) => {
            if (rec.prompt_id === recommendation.prompt_id) {
              return { ...rec, rating: newRating };
            }
            return rec;
          }),
        },
      });
      refetchBookmarks();
    } catch (error) {
      console.error("Error updating rating:", error);
    }
  };

  const handleToggleBookmark = async (recommendation: any) => {
    try {
      const newBookmarkValue = !recommendation.is_bookmarked;
      await updatePathSession({
        id: selectedSession.id,
        data: {
          recommendations: selectedSession?.recommendations?.map((rec: any) => {
            if (rec.prompt_id === recommendation.prompt_id) {
              return { ...rec, is_bookmarked: newBookmarkValue };
            }
            return rec;
          }),
        },
      });
      refetchBookmarks();
    } catch (error) {
      console.error("Error updating bookmark:", error);
    }
  };

  if (isLoadingBookmarks || isLoadingPaths) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader color="blue" size="lg" />
          <Text size="sm" c="dimmed">Loading bookmarks...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box>
      {/* Bookmarks by Path */}
      {Object.keys(filteredBookmarks).length === 0 ? (
        <Center h={200}>
          <Text size="lg" c="dimmed">
            No bookmarks yet.
          </Text>
        </Center>
      ) : (
        Object.entries(filteredBookmarks).map(([pathName, sessions]) => (
          <Card key={pathName} mt="md" withBorder>
            <Stack gap="xs">
              <Group justify="space-between">
                <Text fw={500} c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.indigo[9]}>
                  {pathName}
                </Text>
              </Group>
              
              {sessions.map((session, index) => (
                <Box key={`${session.sessionId}-${index}`}>
                  <Group 
                    justify="space-between" 
                    p="xs" 
                    bg={colorScheme === "dark" ? theme.colors.neutral[8] : theme.colors.neutral[2]}
                    style={{ 
                      cursor: "pointer",
                      borderRadius: "var(--mantine-radius-sm)",
                      transition: "background-color 0.2s ease"
                    }}
                    onClick={() => handleBookmarkClick(session)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                        ? theme.colors.neutral[7] 
                        : theme.colors.neutral[3];
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                        ? theme.colors.neutral[8] 
                        : theme.colors.neutral[2];
                    }}
                  >
                    <Group>
                      <BookmarkSimpleIcon 
                        size={20} 
                        color="blue" 
                      />
                      <Text fw={500} size="sm">
                        {session.sessionTitle}
                      </Text>
                    </Group>
                    
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSessionBookmark(session);
                      }}
                      size="sm"
                    >
                      <XIcon size={16} />
                    </ActionIcon>
                  </Group>
                  {index < sessions.length - 1 && <Divider />}
                </Box>
              ))}
            </Stack>
          </Card>
        ))
      )}

      <Modal
        opened={modalOpen}
        onClose={closeModal}
        withCloseButton={false}
        centered
        size="xl"
        styles={{
          body: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          content: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          header: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
        }}
      >
        {selectedSession && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              height: 500,
              overflowX: "hidden",
              maxWidth: "100%",
              wordWrap: "break-word",
              position: "relative",
            }}
          >
            <Group justify="space-between" align="center" mb="md">
              <Text
                fw={600}
                size="md"
                style={{
                  color: theme.colors.primary[8],
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                {selectedSession?.title || "Session Details"}
              </Text>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={closeModal}
                aria-label="Close"
              >
                <XIcon size={22} />
              </ActionIcon>
            </Group>

            <Box
              style={{
                flex: 1,
                overflowY: "auto",
                maxWidth: "100%",
                wordWrap: "break-word",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <Flex direction={"column"} gap="xs" mb="md">
                {selectedSession.recommendations
                  ?.filter((rec: any) => rec.is_bookmarked)
                  ?.map((recommendation: any) => (
                    <Flex
                      key={recommendation?.prompt_id}
                      id={recommendation?.prompt_id}
                      direction={"column"}
                    >
                      <Flex w={"100%"} justify={"end"}>
                        <Rating
                          value={recommendation?.rating}
                          fractions={2}
                          size="md"
                          onChange={(newRating) => handleUpdateRating(recommendation, newRating)}
                        />
                        <BookmarkSimpleIcon
                          size={22}
                          color={theme.colors.neutral[8]}
                          weight={recommendation?.is_bookmarked ? "fill" : "regular"}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleBookmark(recommendation)}
                        />
                      </Flex>
                      <Text
                        size="sm"
                        style={{
                          lineHeight: 1.6,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "pre-wrap",
                          maxWidth: "100%",
                        }}
                      >
                        {(recommendation?.prompt_text as string)?.replaceAll(
                          "<user_preferred_name>",
                          me?.first_name ?? "User"
                        ).replaceAll("||", "")}
                      </Text>
                      <Divider orientation="horizontal" />
                    </Flex>
                  ))}
              </Flex>
            </Box>
          </Box>
        )}
      </Modal>

      <Modal
        opened={deleteModalOpen}
        onClose={closeDeleteModal}
        title="Delete Bookmark"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this bookmark?
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="outline"
              onClick={cancelDeleteBookmark}
            >
              No
            </Button>
            <Button
              color="red"
              onClick={confirmDeleteBookmark}
            >
              Yes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default SettingsScreen;
